import { useCallback, useEffect, useMemo, useState } from 'react';
import { Platform } from 'react-native';

/**
 * Handlers this hook returns for spreading onto an interactive element.
 *
 * Both the DOM-style (`onMouseEnter`/`onMouseLeave`) and the RN-Pressable-style
 * (`onHoverIn`/`onHoverOut`) handlers are provided so `hoverProps` can be
 * spread onto either a Restyle `Box`/`Text` or a `Pressable` without the caller
 * needing to know which API that element speaks. Real elements listen to one
 * pair or the other — never both — so this does not double-fire.
 */
export type HoverHandlers = {
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onHoverIn?: () => void;
  onHoverOut?: () => void;
};

export type UseHoverOptions = {
  onHoverIn?: () => void;
  onHoverOut?: () => void;
  /** When true, hover is inert: state stays false and no handlers are wired. */
  disabled?: boolean;
};

/**
 * Canonical hover-state hook — the single source of truth for hover behaviour
 * across the product (TA-9005). Hover is a web-only concept, so on native the
 * hook reports `isHovered: false` and returns no handlers.
 *
 * Prefer this over ad-hoc `useState` + `onMouseEnter`/`onMouseLeave` wiring.
 * The `Hoverable`, `SimpleHoverable` and `WebHoverBox` wrappers all delegate
 * here so there is one behaviour to reason about.
 *
 * @example
 * const { isHovered, hoverProps } = useHover();
 * return <Box {...hoverProps} backgroundColor={isHovered ? 'grey01' : 'white'} />;
 */
export const useHover = (options: UseHoverOptions = {}) => {
  const { onHoverIn, onHoverOut, disabled = false } = options;
  const [isHovered, setIsHovered] = useState(false);

  // When hover is disabled mid-hover the handlers are unwired, so hoverOut can
  // never fire — clear the state here so a later re-enable doesn't surface a
  // stale isHovered: true.
  useEffect(() => {
    if (disabled) setIsHovered(false);
  }, [disabled]);

  const hoverIn = useCallback(() => {
    setIsHovered(true);
    onHoverIn?.();
  }, [onHoverIn]);

  const hoverOut = useCallback(() => {
    setIsHovered(false);
    onHoverOut?.();
  }, [onHoverOut]);

  const hoverProps = useMemo<HoverHandlers>(() => {
    if (Platform.OS !== 'web' || disabled) return {};
    return {
      onMouseEnter: hoverIn,
      onMouseLeave: hoverOut,
      onHoverIn: hoverIn,
      onHoverOut: hoverOut,
    };
  }, [disabled, hoverIn, hoverOut]);

  return { isHovered: disabled ? false : isHovered, hoverProps };
};
